import React from 'react';
import PropTypes from 'prop-types';
import useRenderUpgradeNotification from './hooks/use-render-upgrade-notification';

// RDS
import { CloseIcon, EverydollarIcon } from '@ramsey-design-system/icons';
import Button from '@ramsey-design-system/button';
import Heading from '@ramsey-design-system/heading';
import Icon from '@ramsey-design-system/icon';

const LAST_DISMISSED_UPGRADE_BANNER = 'lastDismissedEveryDollarUpgradeBanner';
const HAS_SEEN_UPGRADE_BANNER = 'hasSeenEveryDollarUpgradeBanner';

function EveryDollarUpgradeBanner({ subscriptionEndDate, upgradeUrl }) {
  const { isNotificationShowing, setIsNotificationShowing, daysRemaining } =
    useRenderUpgradeNotification(subscriptionEndDate);

  const handleClose = () => {
    localStorage.setItem(LAST_DISMISSED_UPGRADE_BANNER, Date.now());
    setIsNotificationShowing(false);
  };

  if (!isNotificationShowing) {
    return null;
  }

  const timeRemaining = daysRemaining > 1 ? `${daysRemaining} days` : '1 day';

  return (
    <div
      className="bg-blue-5 border-blue-20 border-solid border-1 p-3 sm:py-4 sm:px-6 rounded-lg relative"
      data-testid="everydollar-notification"
    >
      <div className="flex items-start gap-x-sm sm:gap-x-md">
        <div className="flex bg-green-40 p-1 rounded-full h-4 min-w-4 sm:h-5 sm:w-5 sm:min-w-5 justify-center">
          <Icon icon={EverydollarIcon} size="xlarge" color="light" />
        </div>
        <div>
          <Heading level="3" size="xsmall" className="sm:text-5">
            {daysRemaining === 0
              ? 'Your EveryDollar premium features will end today...'
              : `Just ${timeRemaining} left of those premium features...`}
          </Heading>
          <p className="text-3 sm:text-4 leading-md mt-1 mb-2 max-w-screen-sm">
            You can keep budgeting with limited features in EveryDollar. But to
            keep those premium features (aka bank connect, goals and paycheck
            planning)&mdash;upgrade!
          </p>

          <Button
            href={upgradeUrl}
            target="_blank"
            data-testid="upgrade-button"
            data-logger-element-id="Upgrade to Premium"
          >
            Upgrade to Premium
          </Button>
        </div>

        <Button
          iconOnly
          icon={CloseIcon}
          appearance="neutralSubtle"
          className="absolute top-0 right-0 mt-1 mr-1"
          aria-label="Close"
          onClick={handleClose}
          data-logger-element-id="Close EveryDollar Upgrade Notification"
        />
      </div>
    </div>
  );
}

EveryDollarUpgradeBanner.propTypes = {
  subscriptionEndDate: PropTypes.number.isRequired,
  upgradeUrl: PropTypes.string.isRequired
};

export default EveryDollarUpgradeBanner;
export { LAST_DISMISSED_UPGRADE_BANNER, HAS_SEEN_UPGRADE_BANNER };
